import { useCallback, useEffect, useState } from 'react';
import { clearSession, loadSession, signOut, SIGNED_OUT, watchOtherTabs } from '../src/auth';
import { DEMO_CLIENT_ID } from '../src/config';
import { SignIn } from '../src/SignIn';
import type { Session } from '../src/types';
import { errorText, listClients } from './api';
import type { ClientRow } from './api';
import { AddShop } from './AddShop';
import { CLIENT_TABS, ClientPage } from './Client';
import type { ClientTab } from './Client';
import { Clients } from './Clients';
import { CRM_URL, crmLink, needsYou } from './format';

type Route =
  | { page: 'clients' }
  | { page: 'client'; id: string; tab: ClientTab }
  | { page: 'demo' }
  | { page: 'account' };

/** #clients, #client/<id>, #client/<id>/<tab>, #demo, #account (the phone's menu). */
function parse(hash: string): Route {
  const [a, b, c] = hash.replace(/^#\/?/, '').split('/').map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  if (a === 'client' && b) return { page: 'client', id: b, tab: CLIENT_TABS.some((t) => t.id === c) ? (c as ClientTab) : 'overview' };
  if (a === 'demo') return { page: 'demo' };
  if (a === 'account') return { page: 'account' };
  return { page: 'clients' };
}

function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(window.location.hash));
  useEffect(() => {
    const on = () => {
      const next = parse(window.location.hash);
      setRoute((prev) => {
        // A new page starts at the top; a tab within the same shop keeps your place.
        if (prev.page !== next.page || (prev.page === 'client' && next.page === 'client' && prev.id !== next.id)) window.scrollTo(0, 0);
        return next;
      });
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

const NOT_STAFF = 'This site is for LeadsIRL staff. Contractors sign in at crm.leadsirl.com.';
const isAdmin = (s: Session) => s.groups.includes('admin');

/** A sign-in saved here that isn't LeadsIRL's (it shouldn't happen: the CRMs are separate sites). */
function adminSession(): Session | null {
  const s = loadSession();
  if (s && !isAdmin(s)) {
    clearSession();
    return null;
  }
  return s;
}

/** LeadsIRL's admin CRM at admin.leadsirl.com: every shop, its setup, logins and usage. */
export function Admin() {
  const [session, setSession] = useState<Session | null>(adminSession);

  useEffect(() => {
    // The 30 days are up, or the login was switched off: back to the sign-in screen.
    const out = () => setSession(null);
    window.addEventListener(SIGNED_OUT, out);
    return () => window.removeEventListener(SIGNED_OUT, out);
  }, []);
  useEffect(() => (session ? watchOtherTabs(session, () => setSession(null)) : undefined), [session]);

  if (!session) {
    return <SignIn title="LeadsIRL" subtitle="Admin" refuse={(s) => (isAdmin(s) ? null : NOT_STAFF)} onSignedIn={setSession} />;
  }
  return <Shell session={session} onSignOut={() => { signOut(session); setSession(null); }} />;
}

function Shell({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const route = useRoute();
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setClients(await listClients(session));
      setErr('');
    } catch (e) {
      setErr(errorText(e));
    }
  }, [session]);
  // The list is fetched again whenever you come back to it, so it's never stale for long.
  useEffect(() => {
    if (route.page === 'clients' || clients === null) void load();
    if (route.page === 'clients') document.title = 'Clients · LeadsIRL Admin';
    if (route.page === 'demo') document.title = 'Demo · LeadsIRL Admin';
  }, [route.page, load]);

  const needs = (clients ?? []).filter(needsYou).length;
  const on = (p: Route['page']) => route.page === p || (p === 'clients' && route.page === 'client');
  const link = (p: Route['page'], label: string, badge?: number) => (
    <a href={`#${p}`} className={on(p) ? 'on' : ''} aria-current={on(p) ? 'page' : undefined}>
      {label}
      {badge ? <span className="count" aria-label={`${badge} need you`}>{badge}</span> : null}
    </a>
  );

  return (
    <div className="app">
      <nav className="nav" aria-label="Admin">
        <div className="brand"><b>LeadsIRL</b><span>Admin</span></div>
        {link('clients', 'Clients', needs)}
        {link('demo', 'Demo')}
        <div className="spacer" />
        <div className="me">{session.email}</div>
        <button className="out" type="button" onClick={onSignOut}>Sign out</button>
      </nav>

      <main className="main">
        {route.page === 'clients' && <Clients clients={clients} error={err} onRetry={() => void load()} onAdd={() => setAdding(true)} />}
        {route.page === 'client' && <ClientPage session={session} id={route.id} tab={route.tab} onChanged={() => void load()} />}
        {route.page === 'demo' && <Demo />}
        {route.page === 'account' && <Account email={session.email} onSignOut={onSignOut} />}
      </main>

      <nav className="tabbar" aria-label="Admin">
        {link('clients', 'Clients', needs)}
        {link('demo', 'Demo')}
        {link('account', 'You')}
      </nav>

      {adding && (
        <AddShop
          session={session}
          onClose={() => setAdding(false)}
          onAdded={(c) => {
            setAdding(false);
            void load();
            window.location.hash = `client/${encodeURIComponent(c.clientId)}`;
          }}
        />
      )}
    </div>
  );
}

/** For sales calls: the demo shop's CRM and the assistant, to share on Zoom, Meet or FaceTime. */
function Demo() {
  return (
    <>
      <div className="top"><h1>Demo</h1></div>
      <p className="summary">For sales calls on Zoom, Google Meet or FaceTime: share your screen and show a contractor what they'd get.</p>
      <div className="demo-grid">
        <section className="card">
          <h2>The demo CRM</h2>
          <p>LeadsIRL's own shop, with its leads, calendar and texts, exactly as a contractor sees their CRM.</p>
          <a className="primary" href={crmLink(DEMO_CLIENT_ID, true)} target="_blank" rel="noopener noreferrer">Open the demo CRM <span aria-hidden="true">↗</span></a>
        </section>
        <section className="card">
          <h2>Try the assistant</h2>
          <p>Text the assistant as if you were a homeowner, and let them watch it qualify and book. Nothing goes to a real phone.</p>
          <a className="primary" href={`${CRM_URL}/?sim=1`} target="_blank" rel="noopener noreferrer">Open Try your assistant <span aria-hidden="true">↗</span></a>
        </section>
      </div>
      <p className="fine">Both open at {CRM_URL.replace('https://', '')}, which keeps its own sign-in: sign in there once with this same login, and it lasts 30 days. Contractors never see Try your assistant.</p>
    </>
  );
}

/** The phone's third tab: who's signed in, and Sign out. */
function Account({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <>
      <div className="top"><h1>You</h1></div>
      <section className="card">
        <p className="fine">Signed in as</p>
        <p><b>{email}</b></p>
        <button type="button" className="ghost" onClick={onSignOut}>Sign out</button>
      </section>
    </>
  );
}
